import { z } from 'zod'

const reportStationSnapshotSchema = z.object({
  id: z.number(),
  customer_id: z.number(),
  brand: z.string(),
  model: z.string().nullable(),
  evse_id: z.string().nullable(),
  registration_date: z.string().nullable(),
  total_volume_kwh: z.number().nullish()
})

export const reportListSchema = z.object({
  id: z.number(),
  customer_id: z.number().nullable(),
  report_batch_id: z.number().nullable().optional(),
  customer: z
    .object({
      id: z.number(),
      full_name: z.string().nullable().optional(),
      first_name: z.string().nullable().optional(),
      last_name: z.string().nullable().optional(),
      email: z.string().nullable().optional(),
      connect_id: z.string().nullable().optional()
    })
    .nullable()
    .optional(),
  report_year: z.number().nullable(),
  report_quarter: z.number().nullable(),
  report_from: z.string().nullable(),
  report_to: z.string().nullable(),
  report_name: z.string().nullable().optional(),
  period_start_date: z.string().nullable(),
  period_end_date: z.string().nullable(),
  start_date: z.string().nullable(),
  end_date: z.string().nullable(),
  total_volume_all_stations_kwh: z.number().nullable(),
  calculated_eres: z.number().nullable(),
  booked_eres: z.number().nullable().optional(),
  compensation: z.string().nullable(),
  payment: z.string().nullable().optional(),
  payment_date: z.string().nullable().optional(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable()
})

export type ReportList = z.infer<typeof reportListSchema>

export const reportBatchListSchema = z.object({
  id: z.number(),
  report_id: z.number().nullable(),
  report_name: z.string().nullable().optional(),
  report_date: z.string().nullable(),
  report_from: z.string().nullable(),
  report_to: z.string().nullable(),
  total_customer: z.number().default(0),
  proceeded_customer: z.number().default(0),
  progress: z.number().default(0),
  status: z.enum(['in_progress', 'completed']),
  has_workflow_log: z.boolean().default(false),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable()
})

export type ReportBatchList = z.infer<typeof reportBatchListSchema>

export const reportBatchOverviewSchema = z.object({
  id: z.number(),
  report_name: z.string().nullable().optional(),
  report_from: z.string().nullable(),
  report_to: z.string().nullable(),
  total_customer: z.number().default(0),
  proceeded_customer: z.number().default(0),
  progress: z.number().default(0),
  status: z.enum(['in_progress', 'completed']),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable()
})

export type ReportBatchOverview = z.infer<typeof reportBatchOverviewSchema>

export const exportableReportBatchSchema = z.object({
  id: z.number(),
  report_name: z.string().nullable().optional(),
  report_from: z.string().nullable(),
  report_to: z.string().nullable()
})

export type ExportableReportBatch = z.infer<typeof exportableReportBatchSchema>

export const reportSchema = z.object({
  id: z.number(),
  customer_id: z.number().nullable(),
  customer: z
    .object({
      id: z.number(),
      full_name: z.string().nullable().optional(),
      first_name: z.string().nullable().optional(),
      last_name: z.string().nullable().optional(),
      email: z.string().nullable().optional()
    })
    .nullable()
    .optional(),
  report_year: z.number().nullable(),
  report_quarter: z.number().nullable(),
  report_from: z.string().nullable(),
  report_to: z.string().nullable(),
  report_name: z.string().nullable().optional(),
  period_start_date: z.string().nullable(),
  period_end_date: z.string().nullable(),
  start_date: z.string().nullable(),
  end_date: z.string().nullable(),
  customer_snapshot: z.record(z.string(), z.any()),
  documents_snapshot: z
    .array(z.record(z.string(), z.any()))
    .nullable()
    .optional(),
  total_volume_all_stations_kwh: z.number().nullable(),
  calculated_eres: z.number().nullable(),
  booked_eres: z.number().nullable().optional(),
  compensation: z.string().nullable(),
  payment: z.string().nullable().optional(),
  payment_date: z.string().nullable().optional(),
  stations_snapshot: z.array(reportStationSnapshotSchema).nullable().optional(),
  created_at: z.string().nullable(),
  updated_at: z.string().nullable()
})

export type Report = z.infer<typeof reportSchema>
